// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Sort } from '@/constants/sort';
import { timeRecordListingSchema } from '@/costs/api/types';
import { z } from 'zod';

/**
 * Schema defining the valid properties for sorting time record.
 */
export const timeRecordSortPropertySchema = z.enum([
  'incurredOn', // Date
  'worker', // Person
  'project', // Project
  'category', // Category
  'duration', // Hours (duration)
  'rate', // Rate
  'amount', // Total
]);

export type TimeRecordSortProperty = z.infer<
  typeof timeRecordSortPropertySchema
>;

/**
 * Schema for validating the API response when fetching time record.
 * Wraps the time record listing data in a standard response structure.
 */
const timeRecordResponseSchema = z.object({
  data: timeRecordListingSchema,
  metadata: responseMetadataSchema,
});

export interface TimeRecordFilter {
  pageSize: number;
  pageNumber?: number;
  sort?: Sort<TimeRecordSortProperty>;
  search?: string;
  startDate?: string;
  endDate?: string;
  personIds?: string[];
  projectIds?: string[];
  categories?: string[];
}

/**
 * Serializes a time record filter object into a query string for API requests.
 * This allows for a stable query key for the filter parameters.
 **/
export function serializeTimeRecordFilter(filter?: TimeRecordFilter): string {
  if (!filter) {
    return '';
  }

  const params = new URLSearchParams();

  params.append('limit', filter.pageSize.toString());
  if (filter.pageNumber) {
    params.append('page', filter.pageNumber.toString());
  }
  if (filter.sort) {
    params.append('sortBy', filter.sort.property);
    params.append('sortOrder', filter.sort.direction);
  }
  if (filter.search) {
    params.append('search', filter.search);
  }
  if (filter.startDate) {
    params.append('startDate', filter.startDate);
  }
  if (filter.endDate) {
    params.append('endDate', filter.endDate);
  }
  if (filter.personIds && filter.personIds.length > 0) {
    filter.personIds.forEach((id) => {
      params.append('personId', id);
    });
  }
  if (filter.projectIds && filter.projectIds.length > 0) {
    filter.projectIds.forEach((id) => {
      params.append('projectId', id);
    });
  }
  if (filter.categories && filter.categories.length > 0) {
    filter.categories.forEach((category) => {
      params.append('category', category);
    });
  }

  return params.toString();
}

/**
 * Fetches a list of time records from the API
 *
 * @param filter Optional filter parameters including sort and pagination
 * @returns A promise that resolves to time record results and total count
 * @throws Will throw if the API request fails or response validation fails
 */
export async function getTimeRecords(
  filter: TimeRecordFilter = { pageSize: 50 }
) {
  const serializedFilter = serializeTimeRecordFilter(filter);
  const url = serializedFilter
    ? `/v1/time-record?${serializedFilter}`
    : '/v1/time-record';
  const response = await api.get(url);

  const result = await parseResponse(timeRecordResponseSchema, response.data, {
    schemaName: 'TimeRecords',
    endpoint: url,
  });

  return result.data;
}
